import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { salesResource } from "../../helpers/configRoutes/backend_helper";

const NewSale = () => {
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [xmlFiles, setXmlFiles] = useState([]);
    const [pdfFiles, setPdfFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const dropRef = useRef(null);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        processFiles(files);
    };

    const processFiles = (files) => {
        const newXmls = [];
        const newPdfs = [];

        files.forEach(file => {
            const isXml = file.name.toLowerCase().endsWith(".xml");
            const isPdf = file.name.toLowerCase().endsWith(".pdf");

            if (isXml) {
                const exists = xmlFiles.some(f => f.name === file.name);
                if (!exists || window.confirm(`Ya agregaste "${file.name}". ¿Agregarlo otra vez?`)) {
                    newXmls.push(file);
                }
            }

            if (isPdf) {
                const exists = pdfFiles.some(f => f.name === file.name);
                if (!exists || window.confirm(`Ya agregaste "${file.name}". ¿Agregarlo otra vez?`)) {
                    newPdfs.push(file);
                }
            }
        });

        if (newXmls.length > 0) setXmlFiles(prev => [...prev, ...newXmls]);
        if (newPdfs.length > 0) setPdfFiles(prev => [...prev, ...newPdfs]);
    };

    const handleDelete = (index, type = 'xml') => {
        if (type === 'xml') {
            setXmlFiles(prev => prev.filter((_, i) => i !== index));
        } else {
            setPdfFiles(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dropRef.current.contains(e.relatedTarget)) {
            setDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }
    };

    const handleUpload = async () => {
        setLoader(true);
        const response = await salesResource('postFiles', {
            xmlFiles,
            pdfFiles,
        });

        if (response.status) navigate('/ventas');
        setLoader(false);
    };

    return (
        <div className="p-6 max-w-full mx-auto w-full">
            <button
                onClick={() => navigate('/ventas')}
                className="mb-4 flex items-center gap-2 text-fr-primary hover:underline hover:text-fr-primary-hover transition text-sm font-medium"
            >
                <span className="material-symbols-outlined text-base">arrow_back</span>
                Volver al listado
            </button>

            <div className="flex items-center gap-3 mb-6">
                <span className="material-symbols-outlined text-fr-primary text-3xl">receipt_long</span>
                <div>
                    <h1 className="text-3xl font-bold text-fr-dark">Nuevas Ventas</h1>
                    <p className="text-fr-muted text-sm">Carga archivos XML y PDF para generar ventas automáticamente</p>
                </div>
            </div>

            <div
                ref={dropRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`transition-all duration-300 border-2 border-dashed rounded-xl p-10 text-center ${dragging
                    ? 'border-fr-primary bg-fr-secondary/50 scale-105 shadow-md'
                    : 'border-fr-muted bg-fr-secondary'
                    }`}
            >
                <label
                    htmlFor="fileInput"
                    className="cursor-pointer inline-block px-6 py-3 bg-fr-primary text-fr-light rounded-md hover:bg-fr-primary-hover transition font-medium"
                >
                    Seleccionar archivos XML y PDF
                </label>
                <input
                    id="fileInput"
                    type="file"
                    multiple
                    accept=".xml,.pdf"
                    className="hidden"
                    onChange={handleFileChange}
                />
                <p className="text-sm text-fr-muted mt-3">O arrastra y suelta los archivos aquí</p>
            </div>

            {/* Lista de archivos */}
            {(xmlFiles.length > 0 || pdfFiles.length > 0) && (
                <div className="mt-8 bg-fr-light border border-fr-muted rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-fr-dark mb-4">Archivos seleccionados</h2>
                    <ul className="max-h-60 overflow-auto divide-y divide-fr-muted">
                        {xmlFiles.map((file, index) => (
                            <li key={`xml-${index}`} className="py-2 px-2 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-fr-primary">description</span>
                                    <span className="text-sm text-fr-dark truncate">{file.name}</span>
                                </div>
                                <button
                                    onClick={() => handleDelete(index, 'xml')}
                                    className="text-fr-danger hover:text-red-700 transition"
                                >
                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                </button>
                            </li>
                        ))}
                        {pdfFiles.map((file, index) => (
                            <li key={`pdf-${index}`} className="py-2 px-2 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-red-500">picture_as_pdf</span>
                                    <span className="text-sm text-fr-dark truncate">{file.name}</span>
                                </div>
                                <button
                                    onClick={() => handleDelete(index, 'pdf')}
                                    className="text-fr-danger hover:text-red-700 transition"
                                >
                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {(xmlFiles.length > 0 || pdfFiles.length > 0) && (
                <button
                    onClick={handleUpload}
                    disabled={loader}
                    className={`fixed bottom-6 right-6 z-50 bg-fr-primary text-white rounded-full w-16 h-16 flex items-center justify-center shadow-xl transition transform hover:scale-105 
        ${loader ? 'opacity-60 cursor-not-allowed' : 'hover:bg-fr-primary-hover'}`}
                    title="Guardar venta"
                >
                    {loader ? (
                        <span className="animate-spin material-symbols-outlined text-[24px]">sync</span>
                    ) : (
                        <span className="material-symbols-outlined text-[28px]">save</span>
                    )}
                </button>
            )}
        </div>
    );
};

export default NewSale;
